import type {
  EchoflicksSessionMedia,
  ImageTriageDto,
  ImageTriageFinding,
  ImageTriageGroup,
  ImageTriageObservation,
} from "../../types";

export const IMAGE_TRIAGE_FILTERS = [
  "needs_review",
  "duplicates",
  "variants",
  "similar",
  "resolution",
  "focus",
  "screenshots",
  "all",
] as const;

export type ImageTriageFilter = (typeof IMAGE_TRIAGE_FILTERS)[number];

export const FILTER_LABELS: Record<ImageTriageFilter, string> = {
  needs_review: "Needs review",
  duplicates: "Duplicates",
  variants: "Variants",
  similar: "Similar moments",
  resolution: "Resolution",
  focus: "Focus",
  screenshots: "Screenshots",
  all: "All images",
};

const DUPLICATE_CODES = new Set(["exact_duplicate", "same_pixels"]);
const VARIANT_CODES = new Set(["altered_copy"]);
const REVIEW_CODES = new Set([
  "analysis_failed",
  "detector_skipped_budget",
  "resolution_critical",
  "resolution_watch",
  "expected_upscale_watch",
  "focus_likely_blur",
  "focus_watch",
  "focus_unassessable",
  "possible_screenshot",
  "synthetic_border",
]);

export const observationNeedsStandaloneReview = (
  observation: ImageTriageObservation,
) =>
  observation.status !== "complete" ||
  observation.findings.some((finding) => REVIEW_CODES.has(finding.code));

export function findingMatchesFilter(
  finding: ImageTriageFinding,
  filter: ImageTriageFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "needs_review") return true;
  if (filter === "duplicates") return DUPLICATE_CODES.has(finding.code);
  if (filter === "variants") return VARIANT_CODES.has(finding.code);
  if (filter === "similar") {
    return (
      finding.code === "similar_moment" ||
      finding.code.startsWith("similar_adjudication_")
    );
  }
  if (filter === "resolution") {
    return (
      finding.code.startsWith("resolution_") ||
      finding.code === "expected_upscale_watch"
    );
  }
  if (filter === "focus") return finding.code.startsWith("focus_");
  return ["possible_screenshot", "synthetic_border"].includes(finding.code);
}

export function observationMatchesFilter(
  observation: ImageTriageObservation,
  filter: ImageTriageFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "needs_review") {
    return observation.status !== "complete" || observation.findings.length > 0;
  }
  return observation.findings.some((finding) =>
    findingMatchesFilter(finding, filter),
  );
}

export const isDuplicateGroup = (group: ImageTriageGroup) =>
  ["exact_bytes", "canonical_pixels"].includes(group.group_kind ?? "");

export const isVariantGroup = (group: ImageTriageGroup) =>
  group.group_kind === "altered_copy";

export const isProposedMomentSet = (group: ImageTriageGroup) =>
  group.group_id.startsWith("itadj_");

export const groupCategory = (group: ImageTriageGroup) =>
  isDuplicateGroup(group)
    ? "duplicate"
    : isVariantGroup(group)
      ? "variant"
      : isProposedMomentSet(group)
        ? "proposed"
        : "similar";

export const groupCategoryOrder = (group: ImageTriageGroup) =>
  ({ duplicate: 0, variant: 1, similar: 2, proposed: 3 })[groupCategory(group)];

export const groupMatchesFilter = (
  group: ImageTriageGroup,
  filter: ImageTriageFilter,
) => {
  if (filter === "all") return true;
  if (filter === "needs_review") return true;
  if (filter === "duplicates") return isDuplicateGroup(group);
  if (filter === "variants") return isVariantGroup(group);
  if (filter === "similar") return group.group_kind === "similar_moment";
  return false;
};

export type GroupAdjudication = {
  verdict: string;
  reason: string | null;
  confidence: number | null;
  representative: string | null;
};

/**
 * Whole-set vision verdict for one group, derived from its members' Tier-2
 * adjudication findings (SIM-004). The findings all carry the same shared
 * verdict for a given set id, so the first member finding is authoritative.
 */
export const groupAdjudication = (
  group: ImageTriageGroup,
  dto: ImageTriageDto,
): GroupAdjudication | null => {
  const byId = observationsById(dto);
  for (const member of group.members) {
    const observation = byId.get(member);
    if (!observation) continue;
    for (const finding of observation.findings) {
      if (!finding.code.startsWith("similar_adjudication_")) continue;
      if (finding.evidence.adjudication_group_id !== group.group_id) continue;
      const verdict = finding.evidence.adjudication_verdict;
      if (typeof verdict !== "string" || verdict === "unavailable") continue;
      const reason = finding.evidence.vlm_reason;
      const confidence = finding.evidence.vlm_confidence;
      const representative = finding.evidence.adjudication_representative;
      return {
        verdict,
        reason: typeof reason === "string" && reason.trim() ? reason : null,
        confidence: typeof confidence === "number" ? confidence : null,
        representative:
          typeof representative === "string" ? representative : null,
      };
    }
  }
  return null;
};

/**
 * AI-proposed moment sets (SIM-004): member sets the classical detector did
 * NOT group but a vision review judged to be one moment. Synthesized into
 * group-shaped objects (ids are `itadj_`-prefixed, never real `itg_` groups)
 * so the existing group card can render them.
 */
export const proposedMomentSets = (dto: ImageTriageDto): ImageTriageGroup[] => {
  const members = new Map<string, Set<string>>();
  const anchors = new Map<string, string>();
  for (const observation of dto.observations) {
    for (const finding of observation.findings) {
      if (finding.code !== "similar_adjudication_proposed") continue;
      const setId = finding.evidence.adjudication_group_id;
      if (typeof setId !== "string" || !setId.startsWith("itadj_")) continue;
      const set = members.get(setId) ?? new Set<string>();
      set.add(observation.occurrence_id);
      members.set(setId, set);
      const representative = finding.evidence.adjudication_representative;
      if (typeof representative === "string" && !anchors.has(setId)) {
        anchors.set(setId, representative);
      }
    }
  }
  return [...members.entries()]
    .filter(([, ids]) => ids.size >= 2)
    .map(([setId, ids]) => {
      const sorted = [...ids].sort();
      return {
        group_id: setId,
        group_kind: "similar_moment",
        display_anchor: anchors.get(setId) ?? sorted[0],
        member_count: sorted.length,
        confidence_band: null,
        members: sorted,
        projection_complete: true,
      };
    })
    .sort((left, right) => left.group_id.localeCompare(right.group_id));
};

const priority = (observation: ImageTriageObservation) => {
  const codes = new Set(observation.findings.map((finding) => finding.code));
  if (observation.status !== "complete" || codes.has("analysis_failed"))
    return 0;
  if (codes.has("resolution_critical")) return 1;
  if (codes.has("focus_likely_blur")) return 2;
  if ([...codes].some((code) => DUPLICATE_CODES.has(code))) return 3;
  if ([...codes].some((code) => VARIANT_CODES.has(code))) return 4;
  if (codes.has("similar_moment")) return 5;
  if ([...codes].some((code) => REVIEW_CODES.has(code))) return 6;
  return 7;
};

export const failureFirst = (observations: ImageTriageObservation[]) =>
  [...observations].sort(
    (left, right) =>
      priority(left) - priority(right) ||
      left.source.upload_sequence - right.source.upload_sequence ||
      left.occurrence_id.localeCompare(right.occurrence_id),
  );

export const observationsById = (dto: ImageTriageDto) =>
  new Map(
    dto.observations.map((observation) => [
      observation.occurrence_id,
      observation,
    ]),
  );

export const exactMediaByKey = (media: EchoflicksSessionMedia[]) =>
  new Map(
    media
      .filter((item) => item.s3_key || item.storage_path)
      .map((item) => [String(item.s3_key ?? item.storage_path), item]),
  );

export const groupObservations = (
  group: ImageTriageGroup,
  dto: ImageTriageDto,
) => {
  const byId = observationsById(dto);
  return group.members
    .map((member) => byId.get(member))
    .filter((value): value is ImageTriageObservation => Boolean(value))
    .sort(
      (left, right) =>
        left.source.upload_sequence - right.source.upload_sequence,
    );
};

export function filterResultCount(
  dto: ImageTriageDto,
  filter: ImageTriageFilter,
) {
  const observations = dto.observations.filter((item) =>
    observationMatchesFilter(item, filter),
  );
  const groups = dto.groups.filter((group) =>
    groupMatchesFilter(group, filter),
  );
  return { observations: observations.length, groups: groups.length };
}
